import { Icon, Typography } from "@/components/atoms";
import {
  flightStatusLabels,
  resolveFlightStatusCode,
} from "@/components/organisms/Bookings/common/BookingFlightStatusCode/BookingFlightStatusCode";
import { FlightStatus } from "@/redux/apis/clientFlight/types";
import { BookingFlightStatus } from "@/redux/slices/booking/types";
import { clsx } from "@/utils";

/**
 * The client sheet's flight-status badge (RM-Client 7039-22288 / 7039-22364).
 *
 * A SIBLING of `BookingFlightStatusCode` rather than a variant of it. The operator app's badge is a filled
 * pill with no icon and a bare "Early"; this one is outlined, carries a plane, and says "Flight Early" —
 * which is the whole point of it on a booking sheet, where "Early" alone sits beside a date, a vehicle and
 * a price and names none of them. Bending the operator badge into that shape would add an icon, a border,
 * a prefix and a second palette to a component with six live call sites, none of which want any of it.
 *
 * What is NOT duplicated is the rule: the label and the delay synthesis come from that component, so a
 * flight reading "Delayed" in the operator app cannot read "Late" here.
 *
 * The client's `FlightStatus` (8 members) is a strict subset of the operator's `BookingFlightStatus` (11) —
 * the three extras are exactly the synthesised ones — so it is assignable with no mapping and no cast.
 */
export interface FlightStatusBadgeProps {
  status: FlightStatus;
  /** Minutes late (positive) or early (negative). What turns `scheduled` into "Early". */
  delay?: number;
  /**
   * `md` is the in-card badge (20px plane, 14px label); `sm` is the Booking Summary's (12px plane, 12px
   * label). The design draws the summary label at 10px, which this scale has no step for — `text-sm` is
   * its floor at 12px, and shrinking below it was ruled out.
   */
  size?: "sm" | "md";
}

/**
 * Outlined colours per status, keyed the same way the operator badge keys its filled ones.
 *
 * The design only draws the `early` state (`#EFF8FF` / `#2E90FA` / `#175CD3` = info light / info /
 * info dark). The rest are its siblings in the same token family, so a cancelled flight is not painted
 * "flight early" blue for want of a drawn state. Text is the `dark` step throughout — the design's own
 * choice for `early`, and the readable one on a `light` background.
 */
const badgeColors: Record<BookingFlightStatus, string> = {
  not_found: "border-neutral-mid-gray bg-neutral-surface-gray text-neutral-dark-gray",
  unknown: "border-neutral-mid-gray bg-neutral-surface-gray text-neutral-dark-gray",
  // Never rendered — `flightStatusBadgeShows` withholds the badge for a resolved `scheduled`. Kept so the
  // record stays exhaustive over `BookingFlightStatus`.
  scheduled: "border-success bg-success-light text-success-dark",
  "en-route": "border-success bg-success-light text-success-dark",
  "on-time": "border-success bg-success-light text-success-dark",
  landed: "border-success bg-success-light text-success-dark",
  early: "border-info bg-info-light text-info-dark",
  delayed: "border-warning bg-warning-light text-warning-dark",
  diverted: "border-warning bg-warning-light text-warning-dark",
  incident: "border-warning bg-warning-light text-warning-dark",
  cancelled: "border-danger bg-danger-light text-danger-dark",
};

/**
 * Does this flight have anything to say for itself?
 *
 * A resolved `scheduled` means the feed has told us nothing beyond the published timetable, and a green
 * badge asserting that is worse than no badge — it dresses silence up as a positive claim. Gated on the
 * RESOLVED value, never the raw status: `scheduled` with a non-zero delay resolves to `delayed` / `early`,
 * and a delayed future flight is the one pre-departure case that genuinely carries information.
 *
 * Exported so a caller can ask before laying out around the badge; the badge withholds itself either way.
 */
export const flightStatusBadgeShows = (status: FlightStatus, delay?: number): boolean =>
  resolveFlightStatusCode(status, delay) !== "scheduled";

export const FlightStatusBadge = ({ status, delay, size = "md" }: FlightStatusBadgeProps) => {
  const resolved = resolveFlightStatusCode(status, delay);
  const small = size === "sm";

  if (!flightStatusBadgeShows(status, delay)) return null;

  return (
    <span
      className={clsx(
        "inline-flex w-fit items-center justify-center rounded-lg border",
        small ? "gap-1 px-2 py-px" : "gap-2 px-2 py-0.5",
        badgeColors[resolved]
      )}
    >
      {/* Unrotated, unlike the card's own connector plane: the design draws it climbing away to the
          right, and `rotate-90` would lay it flat across the label. */}
      <Icon name="Airplane" size={small ? 12 : "md"} className="shrink-0" />
      <Typography variant={small ? "caption" : "action"} className="whitespace-nowrap">
        {`Flight ${flightStatusLabels[resolved]}`}
      </Typography>
    </span>
  );
};
